const PlummeSaftExport = (() => {
  const xmlEscape = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

  const number = (value) => Number(value) || 0;
  const money = (value) => Math.abs(number(value)).toFixed(2);
  const accountCode = (value) => String(value ?? '').trim() || '0000';
  const isoDate = (value, fallback) => {
    const raw = String(value || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const norwegian = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (norwegian) return `${norwegian[3]}-${norwegian[2].padStart(2, '0')}-${norwegian[1].padStart(2, '0')}`;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? fallback : parsed.toISOString().slice(0, 10);
  };
  const monthOf = (date) => isoDate(date, '').slice(5, 7) || '01';
  const countryCode = (value) => /^[A-Z]{2}$/.test(String(value || '').toUpperCase())
    ? String(value).toUpperCase()
    : '';

  const resolveSaftVersion = (year) => Number(year) >= 2027 ? '1.40' : '1.30';

  const groupingCategoryByLabel = {
    'Balanseverdi for anleggsmiddel': 'balanseverdiForAnleggsmiddel',
    'Balanseverdi for omløpsmiddel': 'balanseverdiForOmloepsmiddel',
    Egenkapital: 'egenkapital',
    'Kortsiktig gjeld': 'kortsiktigGjeld',
    'Langsiktig gjeld': 'langsiktigGjeld',
    'Annen driftsinntekt': 'annenDriftsinntekt',
    Salgsinntekt: 'salgsinntekt',
    Varekostnad: 'varekostnad',
    'Lønnskostnad': 'loennskostnad',
    'Annen driftskostnad': 'annenDriftskostnad',
    Finansinntekt: 'finansinntekt',
    Finanskostnad: 'finanskostnad',
    Skattekostnad: 'skattekostnad',
  };
  const groupingAliases = {
    1029: '1020', 1089: '1080', 1100: '1115', 1109: '1115', 1110: '1120', 1119: '1120', 1129: '1120',
    1159: '1150', 1200: '1205', 1209: '1205', 1220: '1221', 1229: '1221', 1230: '1238', 1239: '1238',
    1240: '1225', 1249: '1225', 1250: '1280', 1289: '1280', 1396: '1390', 1420: '1400', 1440: '1400', 1460: '1400',
    1572: '1570', 1579: '1570', 1581: '1500', 1700: '1570', 2060: '2050', 2063: '2050', 2070: '2050',
    2240: '2220', 2255: '2250', 2285: '2280', 2291: '2290', 2299: '2290', 2360: '2380', 2390: '2380',
    2540: '2500', 2610: '2600', 2650: '2600', 2690: '2600',
    2700: '2740', 2701: '2740', 2702: '2740', 2703: '2740',
    2710: '2740', 2711: '2740', 2712: '2740', 2713: '2740', 2780: '2770',
    2911: '2910', 2915: '2910', 2930: '2949', 2940: '2949', 2941: '2949', 2960: '2990', 2991: '2990', 2999: '2990',
    3009: '3000', 3010: '3000', 3019: '3000', 3020: '3000', 3029: '3000', 3030: '3000', 3039: '3000',
    3040: '3000', 3049: '3000', 3050: '3000', 3059: '3000', 3061: '3000', 3063: '3000', 3070: '3000',
    3081: '3000', 3090: '3000', 3109: '3100', 3110: '3100', 3119: '3100', 3120: '3100', 3129: '3100',
    3160: '3100', 3170: '3100', 3181: '3100', 3190: '3100', 3209: '3200', 3210: '3200', 3219: '3200',
    3220: '3200', 3229: '3200', 3260: '3200', 3281: '3200', 3290: '3200', 3440: '3400', 3550: '3500',
    3601: '3600', 3605: '3600',
    3609: '3600', 3611: '3695', 3615: '3695', 3619: '3695', 3620: '3695', 3800: '3880', 3881: '3880', 3882: '3880',
    3701: '3700', 3705: '3700', 3707: '3700', 3709: '3700', 3887: '3880', 3889: '3880', 3891: '3885',
    3899: '3885', 3910: '3900', 3971: '3900', 4000: '4005', 4060: '4005',
    4100: '4005', 4200: '4005', 4300: '4005', 4360: '4005', 4090: '4295', 4190: '4295', 4290: '4295', 4390: '4295',
    5090: '5000', 5092: '5000', 5180: '5000', 5200: '5300', 5210: '5300', 5251: '5300', 5280: '5300',
    5290: '5300', 5331: '5300', 5410: '5400', 5800: '5900', 5820: '5900', 5911: '5900', 5915: '5900',
    5919: '5900', 5920: '5900', 5990: '5900', 6010: '6000', 6021: '6000', 6029: '6000', 6030: '6000',
    6540: '6350', 6572: '5900', 6701: '6700', 6725: '6700', 6790: '7700', 6800: '6995', 6840: '6995',
    6860: '6995', 7100: '7155', 7140: '7165', 7150: '7155', 7320: '7330', 7350: '7370', 7360: '7370',
    7400: '7490', 7410: '7490', 7420: '7440', 7430: '7440', 7740: '7700', 7770: '7700', 7798: '7700', 7799: '7700',
    7800: '7880', 7831: '7830', 7835: '7830', 7839: '7830', 7881: '7880', 7882: '7880', 7889: '7880', 8960: '8800', 8961: '8800',
  };
  const officialGroupingMap = () => new Map([
    ...(globalThis.IB_KONTOPLAN || []).flatMap((group) =>
      (group.items || []).map(([code]) => [String(code), groupingCategoryByLabel[group.group]]),
    ),
    ['8800', 'resultatDisponeringForSAF-T'],
  ]);
  const resolveNoSaftGrouping = (account = {}, movement = 0) => {
    const official = officialGroupingMap();
    const accountId = accountCode(account.code || account.accountNumber || account.id);
    const candidates = [
      account.groupingCode, account.yearClosingStandardAccountCode, account.equivalentCode,
      account.standardAccountCode, account.standardAccountId, account.standardAccountID, accountId,
    ].map((value) => String(value || '').trim()).filter(Boolean);
    let groupingCode = candidates.find((candidate) => official.has(candidate));
    if (!groupingCode) {
      const alias = groupingAliases[candidates.find((candidate) => groupingAliases[candidate])];
      groupingCode = alias;
    }
    if (accountId === '8320' && !official.has('8320')) groupingCode = movement >= 0 ? '8321' : '8322';
    if (!groupingCode || !official.has(groupingCode)) {
      throw new Error(`SAF-T-konto ${accountId} mangler gyldig kobling til næringsspesifikasjonen.`);
    }
    return { groupingCategory: official.get(groupingCode), groupingCode };
  };

  const balanceElements = (opening, closing) => {
    const openingTag = opening >= 0 ? 'OpeningDebitBalance' : 'OpeningCreditBalance';
    const closingTag = closing >= 0 ? 'ClosingDebitBalance' : 'ClosingCreditBalance';
    return `<${openingTag}>${money(opening)}</${openingTag}><${closingTag}>${money(closing)}</${closingTag}>`;
  };

  const partyIdentifier = (row) => String(row?.nr || row?.customerNumber || row?.supplierNumber || row?.id || row?.name || '').trim();
  const partyXml = (row, kind, balances = {}) => {
    const tag = kind === 'customer' ? 'Customer' : 'Supplier';
    const idTag = `${tag}ID`;
    const balanceAccount = kind === 'customer' ? '1500' : '2400';
    const idValue = partyIdentifier(row);
    if (!idValue) return '';
    const org = String(row.organizationNumber || row.org || '').replace(/\D/g, '');
    const address = row.address || row.addr || '';
    const contactName = row.contactPerson || row.contactName || row.name || idValue;
    const contact = row.phone || row.email
      ? `<Contact><ContactPerson><FirstName>${xmlEscape(contactName)}</FirstName><LastName>${xmlEscape(contactName)}</LastName></ContactPerson>${row.phone ? `<Telephone>${xmlEscape(row.phone)}</Telephone>` : ''}${row.email ? `<Email>${xmlEscape(row.email)}</Email>` : ''}</Contact>`
      : '';
    const inactive = ['archived', 'passiv', 'inactive'].includes(String(row.status || '').toLowerCase());
    const country = countryCode(row.country);
    const addressXml = address || row.postalPlace || row.city || row.postalCode || row.zip || country
      ? `<Address>${address ? `<StreetName>${xmlEscape(address)}</StreetName>` : ''}${row.postalPlace || row.city ? `<City>${xmlEscape(row.postalPlace || row.city)}</City>` : ''}${row.postalCode || row.zip ? `<PostalCode>${xmlEscape(row.postalCode || row.zip)}</PostalCode>` : ''}${country ? `<Country>${country}</Country>` : ''}</Address>`
      : '';
    return `      <${tag}>${org ? `<RegistrationNumber>${xmlEscape(org)}</RegistrationNumber>` : ''}<Name>${xmlEscape(row.name || idValue)}</Name>${addressXml}${contact}<${idTag}>${xmlEscape(idValue)}</${idTag}><BalanceAccount><AccountID>${balanceAccount}</AccountID>${balanceElements(number(balances.opening), number(balances.closing))}</BalanceAccount><PartyInfo><CurrencyCode>NOK</CurrencyCode><Type>Company</Type><Status>${inactive ? 'Inactive' : 'Active'}</Status></PartyInfo></${tag}>`;
  };

  const openingBalancesFor = (state, year) => {
    const opening = state.openingBalanceByYear?.[String(year)] || {};
    const rows = opening.balanceLines || [];
    const totals = new Map();
    const add = (code, amount) => totals.set(accountCode(code), number(totals.get(accountCode(code))) + number(amount));
    rows.forEach((row) => add(row.accountCode || row.code, number(row.debit) - number(row.credit)));
    const explicit = new Set(rows.map((row) => accountCode(row.accountCode || row.code)));
    const addLegacy = (code, amount) => { if (!explicit.has(accountCode(code))) add(code, amount); };
    (opening.salesByAccount || []).forEach((row) => addLegacy(row.accountCode || row.code || '3000', -Math.abs(number(row.amount))));
    (opening.expensesByAccount || []).forEach((row) => addLegacy(row.accountCode || row.code || '6300', Math.abs(number(row.amount))));
    if (number(opening.outgoingVat)) addLegacy('2700', -Math.abs(number(opening.outgoingVat)));
    if (number(opening.incomingVat)) addLegacy('2710', Math.abs(number(opening.incomingVat)));
    return totals;
  };

  const noSalesTaxCode = (source = {}) => {
    const explicit = String(source.standardTaxCode || source.vatReportCode || source.taxCode || '').trim();
    if (explicit) return explicit;
    const treatment = source.vatTreatment || (number(source.vatRate) === 0 ? 'zeroRated' : 'standard');
    if (treatment === 'salesReverseCharge51') return '51';
    if (treatment === 'export52') return '52';
    if (treatment === 'exempt') return '6';
    if (treatment === 'zeroRated') return '5';
    if (number(source.vatRate) === 15) return '31';
    if (number(source.vatRate) === 12) return '33';
    return '3';
  };
  const noPurchaseTaxCode = (source = {}) => {
    const explicit = String(source.standardTaxCode || source.standardVatCode || source.vatReportCode || source.taxCode || source.purchaseVatCode || source.reverseChargeCode || '').trim();
    if (explicit) return explicit === '23' ? '20' : explicit;
    const treatment = source.vatTreatment || (number(source.vatRate) === 0 ? 'zeroRated' : 'standard');
    if (treatment === 'zeroRated' || treatment === 'exempt') return '0';
    if (number(source.vatRate) === 15) return '11';
    if (number(source.vatRate) === 12) return '13';
    return '1';
  };
  const taxRateForCode = (code, fallback = 0) => ({
    1: 25, 3: 25, 11: 15, 12: 11.11, 13: 12, 14: 25, 15: 15, 21: 25, 22: 15,
    31: 15, 32: 11.11, 33: 12, 81: 25, 82: 25, 83: 15, 84: 15, 86: 25,
    87: 25, 88: 12, 89: 12, 91: 25, 92: 25,
  }[String(code)] ?? number(fallback));
  const supplierIdentity = (source = {}) => {
    const legs = (source.manualPostingLines || []).flatMap(line => ['from', 'to'].filter(side => String(line[`${side}Account`]) === '2400').map(side => ({ type: line[`${side}CounterpartyType`], id: line[`${side}CounterpartyId`] })));
    const ids = legs.every(leg => leg.type === 'supplier' && leg.id) ? [...new Set(legs.map(leg => leg.id))] : [];
    return source.supplierId || source.supplier?.id || source.supplier?.nr || source.supplier?.supplierNumber || (ids.length === 1 ? ids[0] : '');
  };
  const voucherLineMetadata = (voucher, posting, index, partyLookups = {}) => {
    const account = accountCode(posting.nr || posting.accountCode);
    const source = voucher.source || {};
    const rawCustomerId = posting.customerId || posting.saftCustomerId || (account === '1500'
      ? partyIdentifier(source.customer || { id: source.customerId || partyLookups.invoiceCustomers?.get(source.documentId), name: source.customerName })
      : '');
    const rawSupplierId = posting.supplierId || posting.saftSupplierId || (account === '2400'
      ? source.supplier_id || supplierIdentity(source) || partyLookups.expenseSuppliers?.get(source.document_id || source.documentId || voucher.sourceId)
      : '');
    const customerId = partyLookups.customers?.get(String(rawCustomerId)) || rawCustomerId;
    const supplierId = partyLookups.suppliers?.get(String(rawSupplierId)) || rawSupplierId;
    let tax = posting.saftTaxInformation || posting.taxInformation || null;
    // Codes 14/15 describe the VAT amount itself, never a synthetic cost base.
    if (tax && ['14', '15'].includes(String(tax.code)) && !/^271[01]$/.test(account)) tax = null;
    // Recorded journals already carry authoritative VAT links. Inferring tax
    // again on their unlinked balancing lines duplicates the recorded basis.
    if (voucher.eventId) return { customerId, supplierId, tax };
    if (!tax && voucher.sourceType === 'invoice' && /^3/.test(account)) {
      const offset = voucher.postings.slice(0, index).filter((line) => /^3/.test(accountCode(line.nr || line.accountCode))).length;
      const line = (source.lines || [])[offset] || source;
      const code = noSalesTaxCode(line);
      const base = Math.abs(number(posting.amount));
      const rate = taxRateForCode(code, line.vatRate);
      tax = { code, rate, base, amount: base * rate / 100 };
    } else if (!tax && voucher.sourceType === 'expense' && !['2400', '2700', '2710'].includes(account)) {
      const offset = voucher.postings.slice(0, index).filter((line) => !['2400', '2700', '2710'].includes(accountCode(line.nr || line.accountCode))).length;
      const line = (source.purchaseLines || [])[offset] || source;
      const deductionMode = line.vatDeductionMode || source.vatDeductionMode || 'full';
      const deductiblePercent = deductionMode === 'none' ? 0 : deductionMode === 'partial'
        ? Math.max(0, Math.min(100, number(line.deductibleVatPercent ?? source.deductibleVatPercent))) : 100;
      const originalCode = noPurchaseTaxCode(line);
      const code = deductiblePercent > 0 ? originalCode : '0';
      const rate = deductiblePercent > 0 ? taxRateForCode(originalCode, line.vatRate ?? source.vatRate) : 0;
      const explicitNet = line.amountNet ?? (source.purchaseLines?.length ? undefined : source.amountNet);
      const base = explicitNet === undefined ? Math.abs(number(posting.amount)) : Math.abs(number(explicitNet));
      const amount = ['20', '21', '22'].includes(String(code)) ? 0 : base * rate / 100 * deductiblePercent / 100;
      tax = { code, rate, base, amount };
    } else if (!tax && voucher.sourceType === 'otherSale' && /^3/.test(account)) {
      const code = noSalesTaxCode(source);
      const base = Math.abs(number(posting.amount));
      const rate = taxRateForCode(code, source.vatRate);
      tax = { code, rate, base, amount: base * rate / 100 };
    } else if (!tax && Array.isArray(voucher.vatReportCodes) && voucher.vatReportCodes.length === 1 && !['1500', '1920', '2400', '2700', '2710'].includes(account)) {
      const code = String(voucher.vatReportCodes[0]);
      const base = Math.abs(number(posting.amount));
      const rate = taxRateForCode(code);
      tax = { code, rate, base, amount: base * rate / 100 };
    }
    return { customerId, supplierId, tax };
  };
  const taxInformationXml = (tax, postingAmount) => {
    if (!tax || !String(tax.code || '').trim()) return '';
    const side = tax.side === 'debit' || tax.side === 'credit'
      ? tax.side
      : number(postingAmount) >= 0 ? 'debit' : 'credit';
    const amountTag = side === 'debit' ? 'DebitTaxAmount' : 'CreditTaxAmount';
    const base = ['14', '15'].includes(String(tax.code)) ? '' : `<TaxBase>${money(tax.base)}</TaxBase>`;
    return `<TaxInformation><TaxType>MVA</TaxType><TaxCode>${xmlEscape(tax.code)}</TaxCode><TaxPercentage>${number(tax.rate)}</TaxPercentage>${base}<${amountTag}><Amount>${money(tax.amount)}</Amount></${amountTag}></TaxInformation>`;
  };

  const buildSaftFinancialXml = (state = {}, vouchers = [], options = {}) => {
    if (String(state.settings?.country || '').toUpperCase() !== 'NO') {
      throw new Error('SAF-T-eksport er bare tilgjengelig for norske foretak.');
    }
    const org = String(state.settings?.organizationNumber || state.settings?.orgNumber || '').replace(/\D/g, '');
    if (org.length !== 9) throw new Error('SAF-T krever gyldig organisasjonsnummer (9 siffer) i innstillinger.');

    const year = Number(state.activeYear) || new Date().getFullYear();
    // Imported journals are merged from the original Storage archive by the
    // authenticated export Function. The browser builds only Plumme's native
    // continuation; it never tries to reconstruct source journals from report
    // aggregates or the capped preview kept in app state.
    const version = resolveSaftVersion(year);
    const createdDate = isoDate(options.createdDate, new Date().toISOString().slice(0, 10));
    const customers = state.customers || [];
    const suppliers = state.suppliers || [];
    const partyLookupFor = (rows, allowNames = true) => new Map(rows.flatMap((row) => {
      const exportedId = partyIdentifier(row);
      return [row.id, row.nr, row.customerNumber, row.supplierNumber, allowNames ? row.name : null, exportedId]
        .filter(Boolean).map((value) => [String(value), exportedId]);
    }));
    const partyLookups = {
      customers: partyLookupFor(customers), suppliers: partyLookupFor(suppliers, false),
      invoiceCustomers: new Map((state.invoices || []).map((invoice) => [invoice.id, partyIdentifier(invoice.customer)])),
      expenseSuppliers: new Map((state.expenses || []).map((expense) => [expense.id, supplierIdentity(expense)])),
    };
    const ledgerVouchers = (vouchers || []).filter((voucher) => Array.isArray(voucher.postings) && voucher.postings.length);
    const transactions = ledgerVouchers.filter((voucher) => isoDate(voucher.date, '').startsWith(`${year}-`));
    ledgerVouchers.forEach((voucher) => {
      const date = isoDate(voucher.date, '');
      if (!date || (!options.includePriorForPartyBalances && !date.startsWith(`${year}-`))) {
        throw new Error(`SAF-T-bilag ${voucher.num || voucher.id || ''} mangler gyldig dato i regnskapsåret ${year}.`);
      }
      const missingAccount = voucher.postings.some((posting) => !String(posting.nr || posting.accountCode || '').trim());
      if (missingAccount) throw new Error(`SAF-T-bilag ${voucher.num || voucher.id || ''} mangler konto.`);
      const difference = voucher.postings.reduce((sum, posting) => sum + number(posting.amount), 0);
      if (Math.abs(difference) > 0.005) throw new Error(`SAF-T-bilag ${voucher.num || voucher.id || ''} er ikke balansert.`);
    });
    const postingRows = (sourceVouchers) => sourceVouchers.flatMap((voucher, voucherIndex) => voucher.postings.map((posting, lineIndex) => ({
      voucher,
      voucherIndex,
      lineIndex,
      account: accountCode(posting.nr || posting.accountCode),
      description: posting.desc || posting.description || voucher.name || 'Postering',
      amount: number(posting.amount),
      metadata: voucherLineMetadata(voucher, posting, lineIndex, partyLookups),
    })));
    const postings = postingRows(transactions);
    const priorPostings = postingRows(ledgerVouchers.filter((voucher) => isoDate(voucher.date, '') < `${year}-01-01`));

    const standardNames = new Map((globalThis.PLUMME1_NO_CHART_ACCOUNT_SEEDS || []).map(([code, name]) => [String(code), name]));
    const chartByCode = new Map((state.chartAccounts || []).map((account) => [accountCode(account.code || account.accountNumber || account.id), account]));
    postings.forEach((line) => {
      if (!chartByCode.has(line.account)) chartByCode.set(line.account, { code: line.account, name: standardNames.get(line.account) || `Konto ${line.account}` });
    });
    if (customers.length && !chartByCode.has('1500')) chartByCode.set('1500', { code: '1500', name: 'Kundefordringer' });
    if (suppliers.length && !chartByCode.has('2400')) chartByCode.set('2400', { code: '2400', name: 'Leverandørgjeld' });
    if (!chartByCode.size) chartByCode.set('3000', { code: '3000', name: 'Salgsinntekt' });

    const hasExplicitOpening = Object.prototype.hasOwnProperty.call(state.openingBalanceByYear || {}, String(year));
    const openingByCode = openingBalancesFor(state, year);
    if (!hasExplicitOpening) {
      priorPostings.forEach((line) => openingByCode.set(line.account, number(openingByCode.get(line.account)) + line.amount));
    }
    const movementByCode = new Map();
    postings.forEach((line) => movementByCode.set(line.account, number(movementByCode.get(line.account)) + line.amount));
    const accountsXml = [...chartByCode.entries()].sort(([a], [b]) => a.localeCompare(b, 'nb')).map(([code, account]) => {
      const opening = number(openingByCode.get(code));
      const closing = opening + number(movementByCode.get(code));
      const { groupingCategory, groupingCode } = resolveNoSaftGrouping({ ...account, code }, number(movementByCode.get(code)));
      return `        <Account><AccountID>${xmlEscape(code)}</AccountID><AccountDescription>${xmlEscape(account.name || account.description || `Konto ${code}`)}</AccountDescription><GroupingCategory>${xmlEscape(groupingCategory)}</GroupingCategory><GroupingCode>${xmlEscape(groupingCode)}</GroupingCode><AccountType>GL</AccountType>${balanceElements(opening, closing)}</Account>`;
    }).join('\n');

    const partyBalances = (kind, row) => {
      const id = partyIdentifier(row);
      const idKey = kind === 'customer' ? 'customerId' : 'supplierId';
      const account = kind === 'customer' ? '1500' : '2400';
      const movement = (rows) => rows.filter((line) => line.account === account && String(line.metadata[idKey] || '') === id)
        .reduce((sum, line) => sum + line.amount, 0);
      const importedStat = (state.accountingImportPartyStats?.[kind === 'customer' ? 'customers' : 'suppliers'] || [])
        .find((entry) => String(entry.partyName || '').trim().toLowerCase() === String(row.name || '').trim().toLowerCase());
      const importedYear = Number(importedStat?.outstandingYear);
      const importedClosing = (kind === 'customer' ? 1 : -1) * number(importedStat?.outstanding);
      if (importedStat && importedYear === year) return { opening: 0, closing: importedClosing + movement(postings) };
      const relevantPrior = importedStat && importedYear < year
        ? priorPostings.filter((line) => isoDate(line.voucher.date, '') > `${importedYear}-12-31`)
        : priorPostings;
      const opening = (importedStat && importedYear < year ? importedClosing : 0) + movement(relevantPrior);
      return { opening, closing: opening + movement(postings) };
    };
    const customersXml = customers.map((row) => partyXml(row, 'customer', partyBalances('customer', row))).filter(Boolean).join('\n');
    const suppliersXml = suppliers.map((row) => partyXml(row, 'supplier', partyBalances('supplier', row))).filter(Boolean).join('\n');
    const customerMaster = customersXml ? `    <Customers>\n${customersXml}\n    </Customers>\n` : '';
    const supplierMaster = suppliersXml ? `    <Suppliers>\n${suppliersXml}\n    </Suppliers>\n` : '';
    const taxTable = [
      ['0', 'Ingen MVA-behandling', 0, '0'],
      ['1', 'Kjøp, høy sats', 25, '1'],
      ['11', 'Kjøp, middels sats', 15, '11'],
      ['12', 'Kjøp, råfisk', 11.11, '12'],
      ['13', 'Kjøp, lav sats', 12, '13'],
      ['14', 'Innførsels-MVA, høy sats', 25, '14'],
      ['15', 'Innførsels-MVA, middels sats', 15, '15'],
      ['20', 'Innførsel uten MVA-behandling', 0, '20'],
      ['21', 'Innførselsgrunnlag, høy sats', 25, '21'],
      ['22', 'Innførselsgrunnlag, middels sats', 15, '22'],
      ['3', 'Salg, høy sats', 25, '3'],
      ['31', 'Salg, middels sats', 15, '31'],
      ['32', 'Salg, råfisk', 11.11, '32'],
      ['33', 'Salg, lav sats', 12, '33'],
      ['5', 'Salg utenfor MVA-området', 0, '5'],
      ['51', 'Innenlandsk omvendt avgiftsplikt', 0, '51'],
      ['52', 'Utførsel av varer og tjenester', 0, '52'],
      ['6', 'Omsetning utenfor merverdiavgiftsloven', 0, '6'],
      ['7', 'Ingen MVA-behandling for inntekter', 0, '7'],
      ['81', 'Innførsel varer med fradrag, høy sats', 25, '81'],
      ['82', 'Innførsel varer uten fradrag, høy sats', 25, '82'],
      ['83', 'Innførsel varer med fradrag, middels sats', 15, '83'],
      ['84', 'Innførsel varer uten fradrag, middels sats', 15, '84'],
      ['85', 'Innførsel varer uten beregnet MVA', 0, '85'],
      ['86', 'Tjenester fra utlandet med fradrag, høy sats', 25, '86'],
      ['87', 'Tjenester fra utlandet uten fradrag, høy sats', 25, '87'],
      ['88', 'Tjenester fra utlandet med fradrag, lav sats', 12, '88'],
      ['89', 'Tjenester fra utlandet uten fradrag, lav sats', 12, '89'],
      ['91', 'Klimakvoter eller gull med fradrag', 25, '91'],
      ['92', 'Klimakvoter eller gull uten fradrag', 25, '92'],
    ].map(([code, description, rate, standardCode]) => `      <TaxTableEntry><TaxType>MVA</TaxType><Description>Merverdiavgift</Description><TaxCodeDetails><TaxCode>${code}</TaxCode><EffectiveDate>${year}-01-01</EffectiveDate><Description>${xmlEscape(description)}</Description><TaxPercentage>${rate}</TaxPercentage><Country>NO</Country><StandardTaxCode>${standardCode}</StandardTaxCode><BaseRate>100</BaseRate></TaxCodeDetails></TaxTableEntry>`).join('\n');

    const transactionXml = transactions.map((voucher, voucherIndex) => {
      const date = isoDate(voucher.date, '');
      const lines = voucher.postings.map((posting, index) => {
        const amount = number(posting.amount);
        const metadata = voucherLineMetadata(voucher, posting, index, partyLookups);
        return `              <Line><RecordID>${index + 1}</RecordID><AccountID>${xmlEscape(accountCode(posting.nr || posting.accountCode))}</AccountID>${metadata.customerId ? `<CustomerID>${xmlEscape(metadata.customerId)}</CustomerID>` : ''}${metadata.supplierId ? `<SupplierID>${xmlEscape(metadata.supplierId)}</SupplierID>` : ''}<Description>${xmlEscape(posting.desc || posting.description || voucher.name || 'Postering')}</Description>${amount >= 0 ? `<DebitAmount><Amount>${money(amount)}</Amount></DebitAmount>` : `<CreditAmount><Amount>${money(amount)}</Amount></CreditAmount>`}${taxInformationXml(metadata.tax, amount)}</Line>`;
      }).join('\n');
      return `          <Transaction><TransactionID>${xmlEscape(voucher.num || voucher.id || `B-${voucherIndex + 1}`)}</TransactionID><Period>${monthOf(date)}</Period><PeriodYear>${year}</PeriodYear><TransactionDate>${date}</TransactionDate><SourceID>Plumme</SourceID><Description>${xmlEscape(voucher.name || 'Bilag')}</Description><SystemEntryDate>${isoDate(voucher.createdAt, date)}</SystemEntryDate><GLPostingDate>${date}</GLPostingDate>\n${lines}\n          </Transaction>`;
    }).join('\n');

    const debit = postings.filter((line) => line.amount > 0).reduce((sum, line) => sum + line.amount, 0);
    const credit = Math.abs(postings.filter((line) => line.amount < 0).reduce((sum, line) => sum + line.amount, 0));
    const schemaLocation = `Norwegian_SAF-T_Financial_Schema_v_${version}.xsd`;
    const settings = state.settings || {};
    const companyAddress = `<Address><StreetName>${xmlEscape(settings.address || 'Ikke oppgitt')}</StreetName><City>${xmlEscape(settings.postalPlace || 'NA')}</City><PostalCode>${xmlEscape(settings.postalCode || 'NA')}</PostalCode><Country>NO</Country></Address>`;
    const companyContactName = settings.contactPerson || settings.contactName || settings.businessName || 'Plumme-bruker';
    const companyContact = `<Contact><ContactPerson><FirstName>${xmlEscape(companyContactName)}</FirstName><LastName>${xmlEscape(companyContactName)}</LastName></ContactPerson>${settings.phone ? `<Telephone>${xmlEscape(settings.phone)}</Telephone>` : ''}${settings.email ? `<Email>${xmlEscape(settings.email)}</Email>` : ''}</Contact>`;
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<AuditFile xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns="urn:StandardAuditFile-Taxation-Financial:NO" xsi:schemaLocation="urn:StandardAuditFile-Taxation-Financial:NO ${schemaLocation}">
  <Header><AuditFileVersion>${version}</AuditFileVersion><AuditFileCountry>NO</AuditFileCountry><AuditFileDateCreated>${createdDate}</AuditFileDateCreated><SoftwareCompanyName>Plumme</SoftwareCompanyName><SoftwareID>Plumme</SoftwareID><SoftwareVersion>3</SoftwareVersion><Company><RegistrationNumber>${xmlEscape(org)}</RegistrationNumber><Name>${xmlEscape(settings.businessName || 'Plumme')}</Name>${companyAddress}${companyContact}</Company><DefaultCurrencyCode>NOK</DefaultCurrencyCode><SelectionCriteria><SelectionStartDate>${year}-01-01</SelectionStartDate><SelectionEndDate>${year}-12-31</SelectionEndDate></SelectionCriteria><TaxAccountingBasis>A</TaxAccountingBasis></Header>
  <MasterFiles>
    <GeneralLedgerAccounts>
${accountsXml}
    </GeneralLedgerAccounts>
${customerMaster}${supplierMaster}    <TaxTable>
${taxTable}
    </TaxTable>
  </MasterFiles>
  <GeneralLedgerEntries><NumberOfEntries>${transactions.length}</NumberOfEntries><TotalDebit>${money(debit)}</TotalDebit><TotalCredit>${money(credit)}</TotalCredit><Journal><JournalID>PLUMME</JournalID><Description>Plumme</Description><Type>A</Type>
${transactionXml}
        </Journal></GeneralLedgerEntries>
</AuditFile>`;
    return { xml, year, version, voucherCount: transactions.length, postingCount: postings.length };
  };

  return { buildSaftFinancialXml, resolveSaftVersion, resolveNoSaftGrouping };
})();

Object.assign(globalThis, { PlummeSaftExport });
